import { PASSWORD_POLICY } from '@volee/config'
import { AuthError, type AuthErrorCode } from '@volee/auth'
import type { FieldPath, FieldValues, UseFormReturn, UseFormSetError } from 'react-hook-form'

import { authErrorMessage } from '../messages/auth-errors.ts'

// Which input an auth failure belongs to, per flow, and the two checks with no server to
// ask. Outside the components so the no-enumeration rule below is reviewable in one place.

export interface SignInFields {
  readonly email: string
  readonly password: string
}

export interface NewPasswordFields {
  readonly newPassword: string
  readonly confirmPassword: string
}

export interface ResetRequestFields {
  readonly email: string
}

export interface MfaFields {
  readonly code: string
}

export interface ResetConfirmFields {
  readonly code: string
  readonly newPassword: string
  readonly confirmPassword: string
}

export interface ChangePasswordFields {
  readonly currentPassword: string
  readonly newPassword: string
  readonly confirmPassword: string
}

/** A flow's map from failure to input. Absent = the message belongs to the form. */
export type FieldMap<TFields> = Partial<Record<AuthErrorCode, keyof TFields>>

// Deliberately empty, and a test keeps it that way: marking the email or the password would
// say which of the two was wrong, which answers "does this account exist" to anyone asking.
export const SIGN_IN_FIELDS: FieldMap<SignInFields> = {}

export const NEW_PASSWORD_FIELDS: FieldMap<NewPasswordFields> = {
  password_rejected: 'newPassword',
}

// A wrong code is the one auth failure here that belongs to an input: the credentials are
// already accepted at this point, and a dead challenge belongs to the form that comes back.
export const MFA_FIELDS: FieldMap<MfaFields> = {
  code_mismatch: 'code',
  code_expired: 'code',
}

// Here no email is in play, so a refusal *is* the current password being wrong.
export const CHANGE_PASSWORD_FIELDS: FieldMap<ChangePasswordFields> = {
  invalid_credentials: 'currentPassword',
  password_rejected: 'newPassword',
}

/** What a flow's hook returns and its form component takes: one shape, five flows. */
export interface AuthFormProps<TFields extends FieldValues> {
  readonly form: UseFormReturn<TFields>
  readonly submit: (fields: TFields) => void
  /** True while the request is out: the inputs are disabled, not removed. */
  readonly pending: boolean
  /** A failure belonging to no input, already translated. */
  readonly formError?: string
}

export interface AuthFailure<TFields> {
  /** The input to mark. Absent means an Alert above the form: the safe direction. */
  readonly field?: keyof TFields
  readonly message: string
}

/** A failure, placed. Everything goes through the message layer — `message` is never read. */
export function authFailure<TFields>(
  fields: FieldMap<TFields>,
  error: unknown,
): AuthFailure<TFields> {
  const field = error instanceof AuthError ? fields[error.code] : undefined
  return { field, message: authErrorMessage(error) }
}

export interface PasswordTexts {
  readonly required: string
  readonly mismatch: string
  readonly policy: string
}

/** The pool's policy, rule by rule, so the wizard can tick them off while someone types
 * instead of reddening the field on every keystroke. `meetsPolicy` is their conjunction. */
export const PASSWORD_RULES = ['minLength', 'uppercase', 'lowercase', 'digit'] as const

export type PasswordRule = (typeof PASSWORD_RULES)[number]

/** Which rules a password already satisfies. Read from the one place that mirrors the pool,
 * so this is not a second copy of the rules free to drift from Cognito. */
export function passwordRuleResults(password: string): Record<PasswordRule, boolean> {
  return {
    minLength: password.length >= PASSWORD_POLICY.minLength,
    uppercase: !PASSWORD_POLICY.requireUppercase || /[A-Z]/.test(password),
    lowercase: !PASSWORD_POLICY.requireLowercase || /[a-z]/.test(password),
    digit: !PASSWORD_POLICY.requireNumbers || /[0-9]/.test(password),
  }
}

function meetsPolicy(password: string): boolean {
  return Object.values(passwordRuleResults(password)).every(Boolean)
}

/* The checks with no server to ask. The policy is among them on purpose: Cognito answers it
 * only at the end of the wizard, three steps after the field is gone. */
export function newPasswordProblems(
  fields: NewPasswordFields,
  texts: PasswordTexts,
): Partial<Record<keyof NewPasswordFields, string>> {
  if (fields.newPassword === '') return { newPassword: texts.required }
  if (!meetsPolicy(fields.newPassword)) return { newPassword: texts.policy }
  if (fields.confirmPassword === '') return { confirmPassword: texts.required }
  // The confirmation does not exist server-side, so nobody else can answer this.
  if (fields.newPassword !== fields.confirmPassword) {
    return { confirmPassword: texts.mismatch }
  }
  return {}
}

/** Empty credentials: the server's answer to them says nothing a user can use. */
export function signInProblems(
  fields: SignInFields,
  requiredText: string,
): Partial<Record<keyof SignInFields, string>> {
  if (fields.email.trim() === '') return { email: requiredText }
  if (fields.password === '') return { password: requiredText }
  return {}
}

export function resetRequestProblems(
  fields: ResetRequestFields,
  requiredText: string,
): Partial<Record<keyof ResetRequestFields, string>> {
  return fields.email.trim() === '' ? { email: requiredText } : {}
}

/** The same rule with the current password in front of it. */
export function changePasswordProblems(
  fields: ChangePasswordFields,
  texts: PasswordTexts,
): Partial<Record<keyof ChangePasswordFields, string>> {
  if (fields.currentPassword === '') return { currentPassword: texts.required }
  return newPasswordProblems(fields, texts)
}

export function mfaProblems(
  fields: MfaFields,
  requiredText: string,
): Partial<Record<keyof MfaFields, string>> {
  return fields.code.trim() === '' ? { code: requiredText } : {}
}

/** The code, then the same pair rule as everywhere else. */
export function resetConfirmProblems(
  fields: ResetConfirmFields,
  texts: PasswordTexts,
): Partial<Record<keyof ResetConfirmFields, string>> {
  if (fields.code.trim() === '') return { code: texts.required }
  return newPasswordProblems(fields, texts)
}

/** Puts a failure on its input, when it has one. The form-level half is read from `error`. */
export function applyAuthFailure<TFields extends FieldValues>(
  setError: UseFormSetError<TFields>,
  fields: FieldMap<TFields>,
  error: unknown,
): void {
  const failure = authFailure(fields, error)
  if (failure.field !== undefined) {
    setError(failure.field as FieldPath<TFields>, { message: failure.message })
  }
}

/** The message that belongs to no input, or nothing when the failure marked one. */
export function formErrorOf<TFields>(
  fields: FieldMap<TFields>,
  error: unknown,
): string | undefined {
  if (error == null) return undefined
  const failure = authFailure(fields, error)
  return failure.field === undefined ? failure.message : undefined
}

/** Applies the local check that failed, if any. True when the submit has to stop. */
export function applyProblems<TFields extends FieldValues>(
  setError: UseFormSetError<TFields>,
  problems: Partial<Record<keyof TFields, string>>,
): boolean {
  const [field, message] = Object.entries(problems)[0] ?? []
  if (field === undefined || message === undefined) return false
  setError(field as FieldPath<TFields>, { message })
  return true
}
